/**
 * becomingGenerator.ts — Phase 4 client wrapper.
 *
 * Calls the generate-becoming Edge Function with the user's auth token.
 * The Edge Function handles all data loading server-side and deduplicates
 * by calendar month. Returns the narrative or null on any failure.
 *
 * Tier gate is enforced server-side. The client is responsible for only
 * calling this for growth (Practice) and premium (Awakening) users.
 */

import { supabase } from './supabase'

export interface BecomingRecord {
  id?: string
  narrative_text: string
  session_count: number
  generated_at: string
  month_key: string
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string

/**
 * Requests a becoming narrative from the Edge Function.
 * - Returns the narrative record on success.
 * - Returns null if the user has fewer than 3 sessions (204 from server).
 * - Returns null on any error (non-blocking — caller continues without it).
 */
export async function fetchBecoming(): Promise<BecomingRecord | null> {
  try {
    const { data: { session } } = await supabase!.auth.getSession()
    const authToken = session?.access_token
    if (!authToken) return null

    const res = await fetch(`${SUPABASE_URL}/functions/v1/generate-becoming`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({}),
    })

    // 204 = below minimum session threshold — not an error, just not ready yet.
    if (res.status === 204) return null
    // 403 = free tier or other ineligibility — should not happen if caller gates.
    if (res.status === 403) return null
    if (!res.ok) return null

    return (await res.json()) as BecomingRecord
  } catch {
    // Never let a becoming fetch interrupt the journal load.
    return null
  }
}
